import { useState } from 'react';
import { Plus, Pencil, Settings, Trash2, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTiposAtivos, useDeleteTipoAtivo } from '@/hooks/useTiposAtivos';
import { TipoAtivo } from '@/types/tipo-ativo';
import { NewTipoAtivoDialog } from './NewTipoAtivoDialog';
import { EditTipoAtivoDialog } from './EditTipoAtivoDialog';
import { CamposTipoAtivoManager } from './CamposTipoAtivoManager';
import { toast } from 'sonner';
import * as LucideIcons from 'lucide-react';
import { useClientContext } from '@/contexts/ClientContext';

export const TipoAtivoManagement = () => {
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editingTipo, setEditingTipo] = useState<TipoAtivo | null>(null);
  const [managingCamposTipo, setManagingCamposTipo] = useState<TipoAtivo | null>(null);

  const { data: tipos, isLoading } = useTiposAtivos();
  const deleteTipoMutation = useDeleteTipoAtivo();
  const { currentClientName } = useClientContext();

  const handleDelete = async (tipo: TipoAtivo) => {
    if (confirm(`Tem certeza que deseja deletar o tipo "${tipo.nome}"?`)) {
      deleteTipoMutation.mutate(tipo.id);
    }
  };

  const getIcon = (iconName?: string) => {
    if (!iconName) return LucideIcons.Package;
    const Icon = (LucideIcons as any)[iconName];
    return Icon || LucideIcons.Package;
  };

  if (isLoading) {
    return <div className="p-6">Carregando tipos de ativos...</div>;
  }

  if (managingCamposTipo) {
    return (
      <CamposTipoAtivoManager
        tipoAtivo={managingCamposTipo}
        onBack={() => setManagingCamposTipo(null)}
      />
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tipos de Ativos</h1>
          <p className="text-muted-foreground">
            Configure os tipos de ativos e seus campos customizados
          </p>
          {currentClientName && (
            <div className="flex items-center gap-2 mt-2">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="text-sm text-primary font-medium">
                Cliente: {currentClientName}
              </span>
            </div>
          )}
        </div>
        <Button onClick={() => setNewDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Tipo
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tipos?.map((tipo) => {
          const Icon = getIcon(tipo.icone);
          
          return (
            <Card key={tipo.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${tipo.cor}20`, color: tipo.cor }}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{tipo.nome}</CardTitle>
                      {tipo.categoria && (
                        <Badge variant="secondary" className="mt-1">
                          {tipo.categoria}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {!tipo.ativo && (
                    <Badge variant="destructive">Inativo</Badge>
                  )}
                </div>
                {tipo.descricao && (
                  <CardDescription>{tipo.descricao}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setEditingTipo(tipo)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setManagingCamposTipo(tipo)}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Campos
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(tipo)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <NewTipoAtivoDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
      />

      {editingTipo && (
        <EditTipoAtivoDialog
          tipo={editingTipo}
          open={!!editingTipo}
          onOpenChange={(open) => !open && setEditingTipo(null)}
        />
      )}
    </div>
  );
};
